import { Component, Inject, Output, EventEmitter, LOCALE_ID, OnInit, OnDestroy, inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthService } from '@app/core/services/auth-service/auth.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { UserService, OptinexUserDTO, AppLocale } from '@app/core/services/http-services/common/user.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { AccountInfo } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { LocalStorageService } from '@app/core/services/custom-services/localstorage.service';
import { UserRoleEnum } from '@app/shared/helpers/role-based-access.helper';
import { SVGIcon, lockIcon, unlockIcon, menuIcon, launchIcon, gearIcon } from '@progress/kendo-svg-icons';
import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { SelectTenantComponent } from '@app/features/app-controls/select-tenant/select-tenant.component';
import { DialogService } from '@progress/kendo-angular-dialog';
import { UserSettingsDialogComponent } from './user-settings-dialog/user-settings-dialog.component';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-user-settings',
  template: `
    <section class="user-profile">
      <ngx-spinner type="ball-scale-multiple"></ngx-spinner>
      <div
        class="col"
        kendoTooltip
      >
        <img
          [src]="userAvatar"
          (error)="avatarError()"
          alt="Avatar"
          [title]="localeId"
        />
        <span [ngClass]="'fi fi-' + flagCode"></span>
        <div style="flex-grow: 1;"></div>
        <kendo-svg-icon
          [size]="'xlarge'"
          [icon]="gearIcon"
          aria-label="Settings"
          class="pointer"
          (click)="openSettingsDialog()"
        ></kendo-svg-icon>
        <kendo-svg-icon
          [size]="'xlarge'"
          [icon]="menuIcon"
          aria-label="Menu"
          class="pointer"
          (click)="onToggleMenu()"
        ></kendo-svg-icon>
      </div>
      @if (user) {
      <h2>{{ name }} ({{ user?.tennatId }})</h2>
      } @if (user) {
      <h3>{{ account?.username }} ({{ user?.userName }})</h3>
      }
      <div class="row">
        <div style="flex-grow: 1;"></div>
        @if (!user) {
        <kendo-loader themeColor="primary"> </kendo-loader>
        }
        <div style="flex-grow: 1;"></div>
      </div>
      @if (user?.isGlobalAdmin) {
      <app-select-tenant
        [showLable]="true"
        [label]="'App.Tenant'"
        [selectedTenant]="user?.tennatId"
      ></app-select-tenant>
      }
    </section>
  `,
  styleUrls: ['user-settings.component.css'],
  imports: [NgxSpinnerModule, KENDO_TOOLTIP, KENDO_SVGICON, KENDO_LOADER, SelectTenantComponent, NgClass],
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  user: OptinexUserDTO;
  userAvatar: SafeUrl;
  locales: AppLocale[] = [];

  lockIcon: SVGIcon = lockIcon;
  unlockIcon: SVGIcon = unlockIcon;
  menuIcon: SVGIcon = menuIcon;
  launchIcon: SVGIcon = launchIcon;
  gearIcon: SVGIcon = gearIcon;

  account: AccountInfo = null;

  flagCode: string = 'us';

  get name(): string {
    // eslint-disable-next-line dot-notation
    return `${this.account?.idTokenClaims['name']}`;
  }

  private userImgRetrived = false;

  private readonly _destroying$ = new Subject<void>();

  @Output() toggleExpanded = new EventEmitter<{}>();
  @Output() updatedLocale = new EventEmitter<AppLocale>();

  authService: AuthService = inject(AuthService);
  userService: UserService = inject(UserService);
  intlService: IntlService = inject(IntlService);
  spinner: NgxSpinnerService = inject(NgxSpinnerService);
  router: Router = inject(Router);
  private readonly _sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly dialogService: DialogService = inject(DialogService);

  constructor(@Inject(LOCALE_ID) public localeId: string) {
    this.locales = this.userService.locales;
  }

  ngOnInit(): void {
    (async () => {
      let user = await this.userService.getUser();

      this.account = await this.authService.getAccount();

      this.user = user;

      var locale = this.userService.getLocaleByCode(user.languageCode, user.localeCode);

      console.log(`App locale: ${JSON.stringify(locale)}`);
      console.log('User locale:', user);
      this.flagCode = locale?.flag || 'uk';

      //This will be removed in future
      const userRoles = {
        [UserRoleEnum.Developer]: user.isDeveloper,
        [UserRoleEnum.Planner]: user.isPlanner,
        [UserRoleEnum.Operator]: user.isOperator,
      };
      this.localStorageService.setItem(LocalStorageService.userRoles, JSON.stringify(userRoles));

      await this.getUserProfileImg();
    })();
  }

  private async getUserProfileImg() {
    if (this.userImgRetrived) return;

    let profileImage = await firstValueFrom(this.userService.getPofileImage());

    this.userAvatar = this._sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(profileImage));

    this.userImgRetrived = true;
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  avatarError(): void {
    this.userAvatar = 'assets/img/img_avatar.png';
  }

  openSettingsDialog() {
    if (!this.user) return;

    const dialogRef = this.dialogService.open({
      content: UserSettingsDialogComponent,
      width: 400,
      minWidth: 300,
      themeColor: 'primary',
    });

    const componentInstance = dialogRef.content.instance as UserSettingsDialogComponent;

    componentInstance.user = this.user;

    componentInstance.setupForm(this.user);

    dialogRef.result.subscribe((result: { saved: boolean; locale?: AppLocale }) => {
      if (result?.saved && result.locale) {
        this.user.languageCode = result.locale.language;
        this.user.localeCode = result.locale.locale;
        this.updatedLocale.emit(result.locale);
        this.flagCode = result.locale.flag;
      }
    });
  }

  onToggleMenu() {
    this.toggleExpanded.emit();
  }
}
